import type { TransactionRequest, TransactionResult } from '@maipo/agent-protocol';
import type { HistoryManager } from '../core/commands';
import type { EditorDocument, Layer } from '../core/model';
import type { MediaPolicy } from '../core/media-mode';

/**
 * Public addon API for Maipo Studio.
 *
 * Maipo core ships with no addons. Everything here is a seam that a separately
 * distributed bundle can attach to at runtime; nothing in core imports an addon.
 *
 * The boundary is deliberately framework-free: an addon renders into a plain
 * `HTMLElement` and receives plain data plus command constructors. That keeps a
 * third-party bundle independent of Maipo's React version and internal module
 * graph, so this contract can stay stable while the app around it changes.
 */

/**
 * What an addon is allowed to do beyond editing the open document.
 *
 * Maipo advertises itself as local-only. That claim has to remain literally
 * true, so any addon that talks to the network must declare the hosts it will
 * contact; the shell surfaces those hosts instead of the "Local only" badge.
 * An addon that declares nothing is treated as fully local.
 */
export interface AddonCapabilities {
  /** Origins the addon may contact, e.g. `['https://maipo.kua.cl']`. */
  readonly network?: readonly string[];
  /** Human-readable sentence describing what leaves the device, if anything. */
  readonly dataLeavesDevice?: string;
  /**
   * Requests access to narrowly-scoped Maipo Cloud services.
   *
   * This does not expose an access token. The host performs authenticated
   * requests on the addon's behalf, and only installs this bridge for addons
   * that explicitly declare the matching capability.
   */
  readonly maipoCloud?: {
    readonly rasterGeneration?: boolean;
    /**
     * Requests the provider-comparison Raster Lab bridge.
     *
     * Declaring this capability does not imply that the lab is available. The
     * server's workspace-scoped capability response remains authoritative, and
     * deployments must not advertise the lab until that endpoint exists.
     */
    readonly rasterLab?: boolean;
  };
}

export interface AddonCloudWorkspace {
  id: string;
  name: string;
  role: 'OWNER' | 'MEMBER';
  credits: number;
  entitlement: 'ACTIVE' | 'SUSPENDED' | 'CANCELED';
  creditMode?: 'METERED' | 'UNLIMITED';
}

export interface AddonCloudAccount {
  user: {
    id: string;
    email?: string;
    displayName?: string;
  };
  workspaces: AddonCloudWorkspace[];
}

export interface AddonRasterGenerationInput {
  workspaceId: string;
  prompt: string;
  model: string;
  size?: string;
  idempotencyKey: string;
  signal?: AbortSignal;
}

export interface AddonRasterGeneration {
  id: string;
  status: 'PENDING' | 'SUCCEEDED' | 'FAILED' | 'UNKNOWN';
  model: string;
  size: string | null;
  creditsCost: number;
  assetUrl?: string | null;
  resultUrl: string | null;
  providerFallbackUrl?: string | null;
  failureCode: string | null;
  createdAt: string;
  updatedAt: string;
}

export type AddonRasterLabOperation =
  | 'SELECTIVE_EDIT'
  | 'REMOVE_OBJECT'
  | 'REMOVE_BACKGROUND'
  | 'REPLACE_BACKGROUND'
  | 'GENERATE_BACKGROUND'
  | 'UPSCALE';

export type AddonRasterInputRequirement =
  | 'REQUIRED'
  | 'OPTIONAL'
  | 'FORBIDDEN';

export interface AddonRasterLabRoute {
  /** Opaque, server-owned provider/profile/price route identifier. */
  id: string;
  provider: {
    id: string;
    label: string;
  };
  operation: AddonRasterLabOperation;
  profile: {
    id: string;
    label: string;
    description: string;
  };
  inputs: {
    source: 'REQUIRED';
    mask: AddonRasterInputRequirement;
    prompt: AddonRasterInputRequirement;
  };
  options: {
    scaleFactors?: readonly number[];
  };
  output: {
    dimensions: 'SOURCE' | 'SCALED' | 'VARIABLE';
    alpha: 'PRESERVED' | 'MAY_TRANSPARENT' | 'OPAQUE';
  };
  quote: {
    credits: number;
    pricingVersion: string;
  };
}

export interface AddonRasterLabCapabilities {
  schemaVersion: 1;
  /** Changes whenever routes, prices, or policies change. */
  revision: string;
  access: {
    allowed: boolean;
    /**
     * Explicit lab authorization. Workspace ownership alone must never grant
     * this role.
     */
    role: 'RASTER_LAB_TESTER' | 'NONE';
    reason?:
      | 'FEATURE_DISABLED'
      | 'NOT_AUTHORIZED'
      | 'ENTITLEMENT_INACTIVE'
      | 'NO_READY_ROUTES';
  };
  limits: {
    maxInputBytes: number;
    maxPixels: number;
    maxOutputPixels: number;
    maxSelectedRoutes: number;
    sourceContentTypes: readonly string[];
    maskContentType: 'image/png';
  };
  /** Empty whenever access.allowed is false. */
  routes: readonly AddonRasterLabRoute[];
}

export type AddonRasterAssetKind = 'SOURCE' | 'MASK' | 'OUTPUT';

export interface AddonRasterAsset {
  id: string;
  kind: AddonRasterAssetKind;
  state: 'AWAITING_UPLOAD' | 'READY' | 'QUARANTINED';
  contentType: string | null;
  sizeBytes: number | null;
  width: number | null;
  height: number | null;
  sha256: string | null;
  createdAt: string;
}

export interface AddonRasterAssetUploadInput {
  workspaceId: string;
  kind: 'SOURCE' | 'MASK';
  blob: Blob;
  width: number;
  height: number;
  idempotencyKey: string;
  signal?: AbortSignal;
}

export interface AddonRasterLabJobInput {
  workspaceId: string;
  routeId: string;
  capabilitiesRevision: string;
  sourceAssetId: string;
  maskAssetId?: string;
  prompt?: string;
  options?: {
    scaleFactor?: number;
  };
  idempotencyKey: string;
  signal?: AbortSignal;
}

export interface AddonRasterLabJob {
  id: string;
  routeId: string;
  operation: AddonRasterLabOperation;
  provider: {
    id: string;
    label: string;
  };
  profile: {
    id: string;
    label: string;
  };
  status:
    | 'PENDING'
    | 'RUNNING'
    | 'SUCCEEDED'
    | 'FAILED'
    | 'UNKNOWN'
    | 'CANCELED';
  creditsCost: number;
  outputAssetId: string | null;
  providerLatencyMs: number | null;
  failureCode: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AddonRasterLabApi {
  loadCapabilities(
    workspaceId: string,
    signal?: AbortSignal,
  ): Promise<AddonRasterLabCapabilities>;
  uploadAsset(input: AddonRasterAssetUploadInput): Promise<AddonRasterAsset>;
  createJob(input: AddonRasterLabJobInput): Promise<AddonRasterLabJob>;
  getJob(
    workspaceId: string,
    jobId: string,
    signal?: AbortSignal,
  ): Promise<AddonRasterLabJob>;
  downloadAsset(
    workspaceId: string,
    assetId: string,
    signal?: AbortSignal,
  ): Promise<Blob>;
}

export interface AddonCloudApi {
  authState(): Promise<{ configured: boolean; authenticated: boolean }>;
  signIn(): Promise<void>;
  loadAccount(): Promise<AddonCloudAccount>;
  generateRaster(input: AddonRasterGenerationInput): Promise<AddonRasterGeneration>;
  getRasterGeneration(workspaceId: string, generationId: string, signal?: AbortSignal): Promise<AddonRasterGeneration>;
  /**
   * Downloads the archived output through the authenticated Maipo API.
   * Addons never receive the internal object-store URL or cloud credentials.
   */
  downloadRasterGeneration(workspaceId: string, generationId: string, signal?: AbortSignal): Promise<Blob>;
  /** Present only when the addon declared and was granted Raster Lab access. */
  readonly rasterLab?: AddonRasterLabApi;
}

/**
 * A transaction an add-on submits through `AddonContext.dispatch`: the ADR-009
 * transaction envelope without the fields the host fills in. `txId` defaults
 * to a fresh ID and `baseRevision` to the current revision; the target is
 * always the document the context is bound to.
 */
export type AddonTransactionRequest = Omit<TransactionRequest, 'txId' | 'document' | 'baseRevision'> & {
  txId?: string;
  baseRevision?: number;
};

/** Everything an addon may touch. Deliberately narrow. */
export interface AddonContext {
  /**
   * The open document, for reading. Deprecated for mutation: an add-on that
   * edits it in place bypasses History, validation and attribution. Edit
   * through `dispatch` (or, for pixels, the helpers below).
   */
  readonly document: EditorDocument;
  /**
   * The document's history. Deprecated for mutation (API v1 keeps it working):
   * commits made through it are attributed to this add-on in History, but skip
   * the add-on policy that `dispatch` enforces (locks, kinds, limits).
   */
  readonly history: HistoryManager;
  /** Layers currently selected, in document order. */
  readonly activeLayers: readonly Layer[];
  /**
   * Places an RGBA buffer on a new raster layer as one undoable command.
   * The usual way an addon contributes pixels without reaching into internals.
   */
  addRasterLayer(name: string, pixels: Uint8ClampedArray, width: number, height: number): void;
  /** Replaces the active raster layer's pixels as one undoable command. */
  replaceActiveRaster(pixels: Uint8ClampedArray, width: number, height: number): boolean;
  /**
   * Applies ID-addressed intents (ADR-009 §3.1) as ONE undoable History entry,
   * attributed to this add-on (`{ kind: 'addon', id }`) in History and the
   * revision ledger. The add-on policy applies: locked layers and operations
   * the agent contract does not model are refused, with a stable rejection
   * code, and nothing changes. Resolves to the transaction result; it never
   * rejects for a refused transaction. Additive in API v1.
   */
  dispatch(request: AddonTransactionRequest): Promise<TransactionResult>;
  /** Surfaces a transient message in the shell. */
  notify(message: string): void;
  /** Re-renders the host panel after the addon mutates its own state. */
  invalidate(): void;
  /** Present only when the addon declared and was granted a Maipo Cloud capability. */
  readonly cloud?: AddonCloudApi;
}

export interface AddonPanel {
  readonly id: string;
  /** Dock tab label. Keep it to one short word. */
  readonly label: string;
  /** Omitted by API-v1 add-ons means visible only in the All workspace. */
  readonly media?: MediaPolicy;
  /**
   * Renders into `container`. Returns a disposer that must remove listeners and
   * abort in-flight work; the shell calls it when the panel unmounts.
   */
  mount(container: HTMLElement, context: AddonContext): () => void;
}

export interface AddonCommand {
  readonly id: string;
  readonly label: string;
  /** Omitted by API-v1 add-ons means visible only in the All workspace. */
  readonly media?: MediaPolicy;
  run(context: AddonContext): void | Promise<void>;
}

export interface MaipoAddon {
  /** Stable reverse-DNS-ish id, e.g. `kua.recraft`. */
  readonly id: string;
  readonly name: string;
  readonly version: string;
  readonly capabilities?: AddonCapabilities;
  readonly panels?: readonly AddonPanel[];
  readonly commands?: readonly AddonCommand[];
  /** Called once on registration. Throwing here rejects the addon. */
  activate?(): void | Promise<void>;
  deactivate?(): void;
}

/** The object Maipo exposes as `window.maipo`. */
export interface MaipoAddonHost {
  readonly apiVersion: number;
  register(addon: MaipoAddon): void;
  list(): readonly MaipoAddon[];
}

/**
 * Bumped only on a breaking change to the shapes above. An addon compiled
 * against a different major is rejected rather than half-loaded.
 */
export const ADDON_API_VERSION = 1;
